import { Response, type Request } from 'miragejs'
import type { MirageServer } from '../../server.js'
import { createErrorResponse } from '../../utils.js'

/**
 * Handler for updating conversation thread priority
 * POST /conversations/threads/:id/priority
 */
export function handleUpdatePriority(
  this: MirageServer,
  schema: MirageServer['schema'],
  request: Request,
) {
  const id = request.params.id
  const conversation = schema.find('conversation', id)

  if (!conversation) {
    return createErrorResponse({
      status: 404,
      errors: [{ detail: `Conversation thread with id ${id} not found` }],
    })
  }

  let body
  try {
    body = JSON.parse(request.requestBody)
  } catch {
    return createErrorResponse({
      status: 400,
      errors: [{ detail: 'Invalid JSON payload' }],
    })
  }

  const priority = body.data?.attributes?.priority

  if (!priority) {
    return createErrorResponse({
      status: 400,
      errors: [{ detail: 'Priority is required' }],
    })
  }

  conversation.update({ priority })
  return new Response(204)
}

/**
 * Handler for updating conversation thread status
 * POST /conversations/threads/:id/status
 */
export function handleUpdateStatus(
  this: MirageServer,
  schema: MirageServer['schema'],
  request: Request,
) {
  const id = request.params.id
  const conversation = schema.find('conversation', id)

  if (!conversation) {
    return createErrorResponse({
      status: 404,
      errors: [{ detail: `Conversation thread with id ${id} not found` }],
    })
  }

  let body
  try {
    body = JSON.parse(request.requestBody)
  } catch {
    return createErrorResponse({
      status: 400,
      errors: [{ detail: 'Invalid JSON payload' }],
    })
  }

  const status = body.data?.attributes?.status

  if (!status) {
    return createErrorResponse({
      status: 400,
      errors: [{ detail: 'Status is required' }],
    })
  }

  conversation.update({ status })
  return new Response(204)
}

/**
 * Handler for assigning a conversation thread to an employee
 * POST /conversations/threads/:id/employee
 */
export function handleAssignEmployee(
  this: MirageServer,
  schema: MirageServer['schema'],
  request: Request,
) {
  const id = request.params.id
  const conversation = schema.find('conversation', id)

  if (!conversation) {
    return createErrorResponse({
      status: 404,
      errors: [{ detail: `Conversation thread with id ${id} not found` }],
    })
  }

  let body
  try {
    body = JSON.parse(request.requestBody)
  } catch {
    return createErrorResponse({
      status: 400,
      errors: [{ detail: 'Invalid JSON in request body' }],
    })
  }
  const assignedUserData = body.data?.relationships?.assigned_user?.data

  const assignedUser = assignedUserData?.id
    ? schema.find('user', assignedUserData.id)
    : null

  conversation.update({ assignedToUser: assignedUser })

  return new Response(204)
}

/**
 * Handler for assigning a patient to a conversation thread
 * POST /conversations/threads/:id/patient
 */
export function handleAssignPatient(
  this: MirageServer,
  schema: MirageServer['schema'],
  request: Request,
) {
  const id = request.params.id
  const conversation = schema.find('conversation', id)

  if (!conversation) {
    return createErrorResponse({
      status: 404,
      errors: [{ detail: `Conversation thread with id ${id} not found` }],
    })
  }

  let body
  try {
    body = JSON.parse(request.requestBody)
  } catch {
    return createErrorResponse({
      status: 400,
      errors: [{ detail: 'Invalid JSON in request body' }],
    })
  }

  const patientData = body.data?.relationships?.patient?.data

  if (patientData === null || patientData === undefined) {
    conversation.update({ patient: null } as never)
    return new Response(204)
  }

  const patient = patientData.id ? schema.find('patient', patientData.id) : null

  if (!patient) {
    return createErrorResponse({
      status: 404,
      errors: [{ detail: `Patient with id ${patientData.id} not found` }],
    })
  }

  conversation.update({ patient } as never)
  return new Response(204)
}
